import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { Timestamp } from 'firebase/firestore'
import { useAuth } from '@/hooks/useAuth'
import { curatorCommentsService } from '../services/curatorComments.service'
import type { CuratorCommentDocument } from '@/types'

export type Comment = CuratorCommentDocument & { id: string }

/**
 * Subscribes (re-fetches on tick) to the curator-comments thread for a single
 * submission. Adds use optimistic insertion so the UI feels instant; on failure
 * we re-fetch authoritative state instead of trying to surgically remove.
 */
export function useCuratorComments(submissionId: string | null) {
  const { user } = useAuth()
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    if (!submissionId) {
      setComments([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    curatorCommentsService
      .list(submissionId)
      .then(setComments)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load comments.'))
      )
      .finally(() => setLoading(false))
  }, [submissionId, tick])

  const add = useCallback(
    async (body: string) => {
      if (!submissionId) return
      if (!user) {
        setError(withErrorCode('You must be signed in to comment.', 'app/validation'))
        return
      }
      const trimmed = body.trim()
      if (!trimmed) return

      const authorName = user.displayName || user.email || 'Curator'
      const tempId = `temp-${Date.now()}`
      const optimistic: Comment = {
        id: tempId,
        submissionId,
        authorUid: user.uid,
        authorName,
        body: trimmed,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      } as unknown as Comment

      setComments((prev) => [...prev, optimistic])
      try {
        await curatorCommentsService.add(submissionId, {
          authorUid: user.uid,
          authorName,
          body: trimmed,
        })
        refresh()
      } catch (err) {
        setError(formatUserFacingError(err, 'Failed to post comment.'))
        refresh()
      }
    },
    [submissionId, user, refresh]
  )

  const remove = useCallback(
    async (commentId: string) => {
      if (!submissionId) return
      setComments((prev) => prev.filter((c) => c.id !== commentId))
      try {
        await curatorCommentsService.delete(submissionId, commentId)
      } catch (err) {
        setError(formatUserFacingError(err, 'Failed to delete comment.'))
        refresh()
      }
    },
    [submissionId, refresh]
  )

  return { comments, loading, error, add, remove, currentUserId: user?.uid ?? null }
}
